/**
 * 统一导航工具
 * 封装App和H5的路由跳转逻辑
 */

import router from '@/router'
import { tcJumpActivity, tcJumpActivityFinishCurrent, tcFinishCurrentActivity, tcJumpFirstPage, isApp } from '@/utils/bridge'
import { getSearchParam } from '@/utils/urlQuery'
import {
  clearWeComAfterAddMarkers,
  shouldSuppressWeComAfterAdd,
} from '@/utils/dongchaPaymentContext'

/**
 * 导航配置选项
 */
export interface NavigationOptions {
  /** H5环境：是否替换当前历史记录（默认 true = replace） */
  replaceInH5?: boolean
  /** App环境：是否关闭当前页面（默认 false = 不关闭） */
  finishCurrentInApp?: boolean
  /** 是否显示导航栏 (App环境) */
  showNavbar?: boolean
  /** 导航栏标题 (App环境) */
  navBarTitle?: string
  /** 状态栏样式 (App环境) */
  statusBarStyle?: 'light' | 'dark'
}

/**
 * 将路径和参数转换为完整的 URL
 */
function buildFullUrl(
  path: string,
  query?: Record<string, string | number>,
  options?: NavigationOptions,
  hash?: string
): string {
  const origin = window.location.origin
  const params: Record<string, string> = {}

  // 保留当前 URL 中的 source 参数（如果存在）
  const sourceParam = getSearchParam('source')
  if (sourceParam) {
    params.source = sourceParam
  }

  // 合并 query 参数（会覆盖上面保留的 source，如果明确指定了的话）
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      params[key] = String(value)
    })
  }

  // App 环境：添加导航栏配置参数
  if (isApp() && options) {
    if (options.showNavbar !== undefined) {
      params.showNavbar = options.showNavbar ? '1' : '0'
    }
    if (options.navBarTitle) {
      params.navBarTitle = options.navBarTitle
    }
    if (options.statusBarStyle) {
      params.statusBarStyle = options.statusBarStyle
    }
  }

  const queryString = new URLSearchParams(params).toString()
  const hashString = hash || ''
  return `${origin}${path}${queryString ? `?${queryString}` : ''}${hashString}`
}

function normalizeHash(hash?: string): string | undefined {
  if (!hash) return undefined
  return hash.startsWith('#') ? hash : `#${hash}`
}

function parseNavigationTarget(
  to: string | { path: string; query?: Record<string, string | number>; hash?: string }
): { path: string; query?: Record<string, string | number>; hash?: string } {
  if (typeof to !== 'string') {
    return {
      path: to.path,
      query: to.query,
      hash: normalizeHash(to.hash),
    }
  }

  const hashIndex = to.indexOf('#')
  const pathAndSearch = hashIndex >= 0 ? to.slice(0, hashIndex) : to
  const hash = hashIndex >= 0 ? normalizeHash(to.slice(hashIndex + 1)) : undefined
  const queryIndex = pathAndSearch.indexOf('?')

  if (queryIndex < 0) {
    return { path: pathAndSearch, hash }
  }

  const path = pathAndSearch.slice(0, queryIndex) || '/'
  const search = pathAndSearch.slice(queryIndex + 1)
  const query: Record<string, string> = {}
  new URLSearchParams(search).forEach((value, key) => {
    query[key] = value
  })

  return {
    path,
    query: Object.keys(query).length > 0 ? query : undefined,
    hash,
  }
}

/**
 * 统一的页面跳转方法
 * @param to - 目标路径或路由对象
 * @param options - 导航配置选项
 *
 * 使用示例：
 * 1. 简单跳转（默认 replace）：navigateTo('/bill')
 * 2. H5 使用 push：navigateTo('/bill', { replaceInH5: false })
 * 3. App 关闭当前页：navigateTo('/bill', { finishCurrentInApp: true })
 * 4. 完整配置：navigateTo('/bill', { replaceInH5: false, finishCurrentInApp: true })
 */
export function navigateTo(
  to: string | { path: string; query?: Record<string, string | number>; hash?: string },
  options?: NavigationOptions
): void {
  const { path, query, hash } = parseNavigationTarget(to)

  // 默认值：H5 使用 replace
  const opts: NavigationOptions = {
    replaceInH5: true,
    ...options,
  }

  if (isApp()) {
    // App 环境：使用 bridge，传递完整 URL
    const fullUrl = buildFullUrl(path, query, opts, hash)
    console.log('[Navigation] App 跳转到:---------------', fullUrl)
    if (opts.finishCurrentInApp) {
      tcJumpActivityFinishCurrent(fullUrl)
    } else {
      tcJumpActivity(fullUrl)
    }
  } else {
    // H5 环境：使用 router（保留 source 参数）
    const sourceParam = getSearchParam('source')

    // 合并 query 参数，保留 source
    const finalQuery: Record<string, string | number> = {}
    if (sourceParam) {
      finalQuery.source = sourceParam
    }
    if (query) {
      Object.assign(finalQuery, query)
    }

    if (opts.replaceInH5) {
      router.replace({ path, query: finalQuery, hash })
    } else {
      router.push({ path, query: finalQuery, hash })
    }
  }
}

/**
 * 返回上一页或指定页面
 * @param fallbackPath - H5 环境的回退路径（可选）
 * @param query - 路由查询参数（可选）
 *
 * 使用场景：
 * 1. App 环境：直接关闭当前页面，由 Native 控制返回逻辑
 * 2. H5 环境：如果提供 fallbackPath 则跳转到指定页面，否则使用 router.back()
 */
export function navigateBack(
  fallbackPath?: string,
  query?: Record<string, string | number>
): void {
  if (isApp()) {
    // App 环境：直接关闭当前页面
    tcFinishCurrentActivity()
  } else {
    // H5 环境：跳转到指定页面或返回上一页
    if (fallbackPath) {
      // 保留 source 参数
      const sourceParam = getSearchParam('source')

      const finalQuery: Record<string, string | number> = {}
      if (sourceParam) {
        finalQuery.source = sourceParam
      }
      if (query) {
        Object.assign(finalQuery, query)
      }

      router.replace({ path: fallbackPath, query: finalQuery })
    } else {
      router.back()
    }
  }
}

/**
 * 返回首页
 *
 * App 环境：写入 `qx-pending-home-tab` flag → 调 tcJumpFirstPage（Native 只关二级页，
 *           主 Tab WebActivity 保留），主 Tab 容器 H5 实例在回前台时消费 flag 切到 /bill。
 * H5 环境：使用 navigateTo 跳转到首页（自动保留 source 参数）
 */
export function navigateToHome(): void {
  if (isApp()) {
    // 主 Tab 与二级页是两个独立 WebView 实例，本页 router 改不了主 Tab；
    // 借助同源 WebView 共享的 localStorage 传递目标 Tab，由主 Tab 容器消费。
    try {
      localStorage.setItem('qx-pending-home-tab', '/bill')
    } catch {
      // 容错：localStorage 不可写也不阻断关页流程
    }
    tcJumpFirstPage()
  } else {
    if (shouldSuppressWeComAfterAdd(getSearchParam('source'), false)) {
      clearWeComAfterAddMarkers()
    } else {
      try {
        sessionStorage.setItem('qx-wecom-after-add-pending', '1')
        sessionStorage.setItem('qx-wecom-after-add-due-time', String(Date.now() + 3000))
      } catch {
        // sessionStorage 不可用时只影响本次企业微信承接弹窗，不阻断回首页。
      }
    }
    navigateTo('/bill', { replaceInH5: true })
  }
}

export default {
  navigateTo,
  navigateBack,
  navigateToHome,
}
